

import { useState, useCallback, useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"

export const A1_CERT_STORAGE_KEY = "previdenciario_workflow_a1_cert_v1"
const A1_CERT_TTL_MS = 12 * 60 * 60 * 1000

interface StoredA1Certificate {
  fileName: string
  fileType: string
  fileDataUrl: string
  lastModified: number
  password: string
  expiresAt: number
}

interface StoredA1CertificateResolved {
  certFile: File
  certPassword: string
  expiresAt: number
}

const formatDateTime = (date: Date): string =>
  date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })

const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "")
    reader.onerror = () => reject(new Error("Falha ao ler certificado"))
    reader.readAsDataURL(file)
  })

const dataUrlToFile = (dataUrl: string, fileName: string, fileType: string, lastModified: number): File => {
  const parts = dataUrl.split(",")
  if (parts.length < 2) {
    throw new Error("Certificado invalido")
  }

  const binaryString = atob(parts[1])
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i += 1) {
    bytes[i] = binaryString.charCodeAt(i)
  }

  return new File([bytes], fileName, {
    type: fileType || "application/x-pkcs12",
    lastModified,
  })
}

export function readStoredA1Certificate(): StoredA1CertificateResolved | null {
  try {
    const raw = localStorage.getItem(A1_CERT_STORAGE_KEY)
    if (!raw) return null

    const stored = JSON.parse(raw) as StoredA1Certificate
    if (!stored?.expiresAt || stored.expiresAt <= Date.now()) {
      localStorage.removeItem(A1_CERT_STORAGE_KEY)
      return null
    }

    const certFile = dataUrlToFile(
      stored.fileDataUrl,
      stored.fileName,
      stored.fileType,
      stored.lastModified
    )

    return {
      certFile,
      certPassword: stored.password || "",
      expiresAt: stored.expiresAt,
    }
  } catch {
    localStorage.removeItem(A1_CERT_STORAGE_KEY)
    return null
  }
}

export function CertificadoUpload() {
  const [a1Certificate, setA1Certificate] = useState<File | null>(null)
  const [a1Password, setA1Password] = useState("")
  const [a1ExpiresAt, setA1ExpiresAt] = useState<Date | null>(null)
  const [a1StorageError, setA1StorageError] = useState<string | null>(null)
  const [isA1Hydrated, setIsA1Hydrated] = useState(false)
  const a1InputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    const restored = readStoredA1Certificate()
    if (restored) {
      setA1Certificate(restored.certFile)
      setA1Password(restored.certPassword)
      setA1ExpiresAt(new Date(restored.expiresAt))
    }
    setIsA1Hydrated(true)
  }, [])

  useEffect(() => {
    if (!isA1Hydrated) return

    if (!a1Certificate || !a1Password.trim()) {
      localStorage.removeItem(A1_CERT_STORAGE_KEY)
      setA1ExpiresAt(null)
      return
    }

    let cancelled = false

    const persist = async () => {
      try {
        const fileDataUrl = await fileToDataUrl(a1Certificate)
        if (cancelled) return

        const expiresAt = Date.now() + A1_CERT_TTL_MS
        const payload: StoredA1Certificate = {
          fileName: a1Certificate.name,
          fileType: a1Certificate.type,
          fileDataUrl,
          lastModified: a1Certificate.lastModified,
          password: a1Password,
          expiresAt,
        }

        localStorage.setItem(A1_CERT_STORAGE_KEY, JSON.stringify(payload))
        setA1ExpiresAt(new Date(expiresAt))
      } catch {
        setA1StorageError("Nao foi possivel salvar o certificado localmente.")
      }
    }

    persist()

    return () => {
      cancelled = true
    }
  }, [a1Certificate, a1Password, isA1Hydrated])

  const clearA1Certificate = useCallback(() => {
    setA1Certificate(null)
    setA1Password("")
    setA1ExpiresAt(null)
    setA1StorageError(null)
    localStorage.removeItem(A1_CERT_STORAGE_KEY)
    if (a1InputRef.current) {
      a1InputRef.current.value = ""
    }
  }, [])

  const handleA1FileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null
    if (!file) return

    const isPfx = file.name.toLowerCase().endsWith(".pfx")
    if (!isPfx) {
      setA1StorageError("Use um arquivo .pfx (certificado A1).")
      event.target.value = ""
      return
    }

    setA1StorageError(null)
    setA1Certificate(file)
  }, [])

  return (
    <section className="mb-6 rounded-xl border border-border bg-card p-4 sm:p-5">
      <div className="flex flex-col gap-4">
        <div className="min-w-0">
          <h2 className="text-sm sm:text-base font-semibold text-foreground break-words">
            Certificado A1 (.pfx) do fluxo
          </h2>
          <p className="text-xs text-muted-foreground break-words">
            O certificado fica salvo apenas por tempo curto (ate 12h) para este fluxo. Se remover arquivo ou senha, ele sai na hora.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor="a1-certificado" className="text-xs font-medium text-muted-foreground">
              Arquivo .pfx
            </label>
            <input
              id="a1-certificado"
              ref={a1InputRef}
              type="file"
              accept=".pfx,application/x-pkcs12"
              onChange={handleA1FileChange}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground file:mr-3 file:rounded-md file:border-0 file:bg-accent file:px-2 file:py-1 file:text-xs file:font-medium"
            />
            <p className="text-xs text-muted-foreground break-words">
              {a1Certificate ? `Selecionado: ${a1Certificate.name}` : "Nenhum certificado selecionado."}
            </p>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="a1-senha" className="text-xs font-medium text-muted-foreground">
              Senha do certificado
            </label>
            <input
              id="a1-senha"
              type="password"
              value={a1Password}
              onChange={(event) => {
                setA1Password(event.target.value)
                if (a1StorageError) setA1StorageError(null)
              }}
              placeholder="Digite a senha do certificado"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-muted-foreground break-words">
            {a1Certificate && a1Password && a1ExpiresAt
              ? `Certificado pronto para o fluxo. Expira em ${formatDateTime(a1ExpiresAt)}.`
              : "Adicione certificado e senha para manter no fluxo."}
          </div>
          <Button variant="outline" size="sm" onClick={clearA1Certificate} disabled={!a1Certificate && !a1Password}>
            Remover certificado
          </Button>
        </div>

        {a1StorageError && (
          <p className="text-xs text-destructive break-words">{a1StorageError}</p>
        )}
      </div>
    </section>
  )
}
